import React from 'react';
import { useMutation } from '@apollo/client';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
// Api
import {
  ADMIN_CLEAR_DELETE_REQUEST,
  ADMIN_DELETE_ACCOUNT,
} from 'api/users/mutations';
// Types
import {
  AdminDeleteAccount,
  AdminDeleteAccountVariables,
} from 'api/users/types/AdminDeleteAccount';
import {
  AdminClearDeleteRequest,
  AdminClearDeleteRequestVariables,
} from 'api/users/types/AdminClearDeleteRequest';
// Helpers
import { getProperErrorMessage } from 'helpers/errors';
import { getBlockingOffersFromError } from 'helpers/accountDeletion';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

type UseAdminDeleteAccountResult = {
  confirmDeleteAccount: (userId: string, name: string) => void;
  confirmClearDeleteRequest: (userId: string, name: string) => void;
};

export const useAdminDeleteAccount = (
  onAccountChanged: () => unknown
): UseAdminDeleteAccountResult => {
  const [adminDeleteAccount] = useMutation<
    AdminDeleteAccount,
    AdminDeleteAccountVariables
  >(ADMIN_DELETE_ACCOUNT);
  const [adminClearDeleteRequest] = useMutation<
    AdminClearDeleteRequest,
    AdminClearDeleteRequestVariables
  >(ADMIN_CLEAR_DELETE_REQUEST);

  const deleteAccount = async (userId: string, name: string) => {
    try {
      await adminDeleteAccount({ variables: { input: { userId } } });
      successNotification(`Account of "${name}" has been deleted`);
      Promise.resolve(onAccountChanged()).catch(() => undefined);
    } catch (err) {
      const blockingOffers = getBlockingOffersFromError(err);

      if (blockingOffers.length) {
        Modal.error({
          title: `"${name}" has campaign offers in progress`,
          content: (
            <>
              <p>Resolve these offers before deleting the account:</p>
              <ul>
                {blockingOffers.map((offer) => (
                  <li key={offer.offerId}>
                    {offer.campaignName} ({offer.status})
                  </li>
                ))}
              </ul>
            </>
          ),
        });
        return;
      }

      errorNotification(
        getProperErrorMessage(err, 'Could not delete the account')
      );
    }
  };

  const confirmDeleteAccount = (userId: string, name: string) => {
    Modal.confirm({
      title: `Delete the account of "${name}"?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "They'll be removed from campaign offers, SMS, email and HubSpot, and won't be able to log in.",
      okText: 'Delete account',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => deleteAccount(userId, name),
    });
  };

  const clearDeleteRequest = async (userId: string, name: string) => {
    try {
      await adminClearDeleteRequest({ variables: { input: { userId } } });
      successNotification(`Deletion request of "${name}" has been cleared`);
      Promise.resolve(onAccountChanged()).catch(() => undefined);
    } catch (err) {
      errorNotification(
        getProperErrorMessage(err, 'Could not clear the deletion request')
      );
    }
  };

  const confirmClearDeleteRequest = (userId: string, name: string) => {
    Modal.confirm({
      title: `Clear the deletion request of "${name}"?`,
      icon: <ExclamationCircleOutlined />,
      content:
        'Use this when they changed their mind about deleting the account.',
      okText: 'Clear request',
      cancelText: 'Cancel',
      onOk: () => clearDeleteRequest(userId, name),
    });
  };

  return { confirmDeleteAccount, confirmClearDeleteRequest };
};
